import { useCallback, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, PointerEvent, SetStateAction } from 'react';
import type { FreeCropCorner } from '../../types/freeCrop';
import { getScaleToPreview } from '../../utils/freeCropUtils';

type UseFreeCropInteractionParams = {
  previewCanvasRef: MutableRefObject<HTMLCanvasElement | null>;
  naturalSize: { width: number; height: number } | null;
  showPreview: boolean;
  getCornerAtPoint: (x: number, y: number) => number | null;
  getEdgeAtPoint: (x: number, y: number) => number | null;
  clampCorner: (corner: FreeCropCorner, index: number) => FreeCropCorner;
  setCorners: Dispatch<SetStateAction<FreeCropCorner[]>>;
  drawPreview: () => void;
};

type DragTarget = { type: 'corner'; index: number } | { type: 'edge'; index: number };

type UseFreeCropInteractionResult = {
  isDragging: boolean;
  handlePointerDown: (event: PointerEvent<HTMLCanvasElement>) => void;
  handlePointerMove: (event: PointerEvent<HTMLCanvasElement>) => void;
  handlePointerUp: (event: PointerEvent<HTMLCanvasElement>) => void;
  handlePointerLeave: (event: PointerEvent<HTMLCanvasElement>) => void;
};

export const useFreeCropInteraction = ({
  previewCanvasRef,
  naturalSize,
  showPreview,
  getCornerAtPoint,
  getEdgeAtPoint,
  clampCorner,
  setCorners,
  drawPreview,
}: UseFreeCropInteractionParams): UseFreeCropInteractionResult => {
  const [dragTarget, setDragTarget] = useState<DragTarget | null>(null);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const pointerIdRef = useRef<number | null>(null);

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      if (showPreview || !previewCanvasRef.current) return;
      const cornerIndex = getCornerAtPoint(event.clientX, event.clientY);
      const edgeIndex = cornerIndex === null ? getEdgeAtPoint(event.clientX, event.clientY) : null;
      if (cornerIndex !== null) {
        setDragTarget({ type: 'corner', index: cornerIndex });
        pointerIdRef.current = event.pointerId;
        lastPointerRef.current = { x: event.clientX, y: event.clientY };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
      } else if (edgeIndex !== null) {
        setDragTarget({ type: 'edge', index: edgeIndex });
        pointerIdRef.current = event.pointerId;
        lastPointerRef.current = { x: event.clientX, y: event.clientY };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
      }
    },
    [getCornerAtPoint, getEdgeAtPoint, previewCanvasRef, showPreview],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      if (
        showPreview ||
        dragTarget === null ||
        !previewCanvasRef.current ||
        !lastPointerRef.current ||
        !naturalSize
      ) {
        return;
      }
      event.preventDefault();

      const canvas = previewCanvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const deltaX = (event.clientX - lastPointerRef.current.x) * scaleX;
      const deltaY = (event.clientY - lastPointerRef.current.y) * scaleY;
      const { scaleX: scaleToPreviewX, scaleY: scaleToPreviewY } = getScaleToPreview({
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        naturalWidth: naturalSize.width,
        naturalHeight: naturalSize.height,
      });

      setCorners((prevCorners) => {
        const newCorners = [...prevCorners];
        if (dragTarget.type === 'corner') {
          const newCorner = {
            x: prevCorners[dragTarget.index].x + deltaX / scaleToPreviewX,
            y: prevCorners[dragTarget.index].y + deltaY / scaleToPreviewY,
          };
          newCorners[dragTarget.index] = clampCorner(newCorner, dragTarget.index);
        } else {
          const firstCornerIndex = dragTarget.index;
          const secondCornerIndex = (dragTarget.index + 1) % prevCorners.length;
          [firstCornerIndex, secondCornerIndex].forEach((cornerIndex) => {
            const newCorner = {
              x: prevCorners[cornerIndex].x + deltaX / scaleToPreviewX,
              y: prevCorners[cornerIndex].y + deltaY / scaleToPreviewY,
            };
            newCorners[cornerIndex] = clampCorner(newCorner, cornerIndex);
          });
        }
        return newCorners;
      });

      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      drawPreview();
    },
    [
      clampCorner,
      dragTarget,
      drawPreview,
      naturalSize,
      previewCanvasRef,
      setCorners,
      showPreview,
    ],
  );

  const stopDragging = useCallback((event: PointerEvent<HTMLCanvasElement>) => {
    if (dragTarget !== null && pointerIdRef.current !== null) {
      event.currentTarget.releasePointerCapture(pointerIdRef.current);
      pointerIdRef.current = null;
    }
    setDragTarget(null);
    lastPointerRef.current = null;
  }, [dragTarget]);

  const handlePointerUp = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      stopDragging(event);
    },
    [stopDragging],
  );

  const handlePointerLeave = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      stopDragging(event);
    },
    [stopDragging],
  );

  return {
    isDragging: dragTarget !== null,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerLeave,
  };
};
